import {
  customFieldsCreateMutation,
  customFieldsListInfiniteQueryKey,
  customFieldsListQueryKey,
  customFieldsPartialUpdateMutation,
  customFieldsRetrieveOptions,
  customFieldsRetrieveQueryKey,
  Type83fEnum
} from '@crm/api'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { allLangs, allLangsValues, useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App, Form, Modal, Tabs } from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { DefaultOptionType } from 'antd/es/select'
import { ModalProps } from 'antd/lib'
import { TFunction } from 'i18next'
import { useEffect, useState } from 'react'

import { FormNomenclaturesCustomFields } from './FormNomenclaturesCustomFields'

type Props = {
  id?: number
  onCancel: () => void
} & ModalProps

const getModalText = (hasId: boolean, t: TFunction) => {
  return {
    title: hasId
      ? t('nomenclature.edit_custom_field')
      : t('nomenclature.add_custom_field'),
    button: hasId ? t('buttons.edit') : t('buttons.add')
  }
}

type LanguageEnum = (typeof allLangsValues)[number]

export const NomenclaturesCustomFieldsModal = ({
  id,
  onCancel,
  ...props
}: Props) => {
  const { t } = useLocales()
  const [value, setValue] = useState<LanguageEnum>(allLangs[0].value)

  const { invalidateQueries } = useInvalidatePredicateQueries()
  const queryClient = useQueryClient()
  const { notification } = App.useApp()

  const [form] = Form.useForm()

  const { title, button } = getModalText(!!id, t)

  const { data, isLoading } = useQuery({
    ...customFieldsRetrieveOptions({
      path: { id: Number(id) }
    }),
    enabled: !!id
  })

  const { mutate: createMutate, isPending: isCreatePending } = useMutation({
    ...customFieldsCreateMutation(),
    onSuccess: () => onSuccessMutation(),
    onError: (error) => showApiErrors(error)
  })

  const { mutate: updateMutate, isPending: isUpdatePending } = useMutation({
    ...customFieldsPartialUpdateMutation(),
    onSuccess: () => onSuccessMutation(),
    onError: (error) => showApiErrors(error)
  })

  const onSuccessMutation = () => {
    onCancel()
    form.resetFields()
    invalidateQueries([
      customFieldsListQueryKey(),
      customFieldsListInfiniteQueryKey()
    ])
    queryClient.removeQueries({
      queryKey: customFieldsRetrieveQueryKey({
        path: { id: Number(id) }
      })
    })
    notification.success({
      message: t('success.success'),
      description: t('success.success_data_save')
    })
  }

  const onSubmit = async () => {
    await form.validateFields()
    const values = form.getFieldsValue(true)

    const missingLanguages: LanguageEnum[] = []
    allLangs.forEach((lang) => {
      const keyField = `key_${lang.value}`
      if (!values[keyField] || values[keyField].trim() === '') {
        missingLanguages.push(lang.value as LanguageEnum)
      }
    })

    if (missingLanguages.length > 0) {
      const firstMissingLang = missingLanguages[0]
      setValue(firstMissingLang)

      setTimeout(() => {
        const fieldName = `key_${firstMissingLang}`

        form.setFields([
          {
            name: fieldName,
            errors: [t('account_settings.this_field_is_required')]
          }
        ])
      }, 200)

      return false
    }

    if (
      values?.validation_rules?.min &&
      values?.validation_rules?.max &&
      values?.validation_rules?.min >= values?.validation_rules?.max
    ) {
      form.setFields([
        {
          name: ['validation_rules', 'min'],
          errors: [t('nomenclature.min_must_be_less_than_max')]
        }
      ])
      return false
    }

    const body = {
      ...values,
      ...(values?.type === Type83fEnum.SELECT &&
        values?.default_value && {
          default_value: values?.options?.find(
            (option: DefaultOptionType) =>
              option?.label === values?.default_value
          )
        }),
      ...(values?.type === Type83fEnum.MULTISELECT && {
        default_value:
          values?.default_value?.length > 0
            ? values?.options
                ?.map((option: DefaultOptionType) =>
                  values?.default_value?.includes(option?.label) ? option : null
                )
                .filter(Boolean)
            : undefined
      })
    }

    if (id) {
      updateMutate({
        path: { id: Number(id) },
        body
      })
    } else {
      createMutate({
        body
      })
    }
  }

  const onTabChange = (value: string) => {
    setValue(value as LanguageEnum)
  }

  const items = allLangs.map((lang) => ({
    label: lang.label,
    key: lang.value
  }))

  useEffect(() => {
    if (data) {
      const defaultValue = data?.default_value as AnyObject

      form.setFieldsValue({
        ...data,
        ...(data?.type === Type83fEnum.SELECT && {
          default_value: defaultValue?.label
        }),
        ...(data?.type === Type83fEnum.MULTISELECT && {
          default_value: defaultValue?.map(
            (item: DefaultOptionType) => item?.label
          )
        })
      })
    }
  }, [data, form])

  return (
    <Modal
      onCancel={onCancel}
      open
      loading={isLoading}
      title={title}
      okButtonProps={{
        htmlType: 'submit',
        onClick: onSubmit,
        loading: isCreatePending || isUpdatePending
      }}
      okText={button}
      width={800}
      {...props}
    >
      <Form layout="vertical" form={form} onFinish={onSubmit}>
        <Tabs activeKey={value} items={items} onChange={onTabChange} />

        <FormNomenclaturesCustomFields lang={value} form={form} isEdit={!!id} />
      </Form>
    </Modal>
  )
}
